import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  FormGroup,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import Pagination from "../../components/Pagination/Pagination";
import {
  confirmDeleteBank,
  createOrUpdateBank,
  getBanks as fetchBanks,
} from "./services/BankService";
import { ConfirmDialog } from "primereact/confirmdialog";

const Banks = () => {
  document.title = "Banks | Valitan IMS";

  const [banks, setBanks] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [bankName, setBankName] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [editingBankId, setEditingBankId] = useState(null);
  const [errors, setErrors] = useState({ name: "", code: "" });

  const [paginationInfo, setPaginationInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0,
    pageSize: 10,
    sort: {},
  });

  useEffect(() => {
    loadBanks(0, paginationInfo.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationInfo.pageSize]);

  const loadBanks = async (page = paginationInfo.pageNumber, size = paginationInfo.pageSize) => {
    const { banks, paginationInfo } = await fetchBanks(page, size);
    setBanks(banks || []);
    setPaginationInfo(
      paginationInfo || {
        totalElements: 0,
        totalPages: 0,
        pageNumber: 0,
        pageSize: size,
        sort: {},
      }
    );
  };

  const filteredBanks = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return banks;
    return banks.filter(
      (bank) =>
        bank?.name?.toLowerCase().includes(query) ||
        bank?.code?.toLowerCase().includes(query)
    );
  }, [banks, searchText]);

  const toggleModal = () => {
    setModalOpen((prev) => !prev);
    if (!modalOpen) {
      setBankName("");
      setBankCode("");
      setErrors({ name: "", code: "" });
      setEditingBankId(null);
    }
  };

  const openModalForEdit = (bank) => {
    setEditingBankId(bank.id);
    setBankName(bank.name ?? "");
    setBankCode(bank.code ?? "");
    setModalOpen(true);
  };

  const handleNextPage = () => {
    if (paginationInfo.pageNumber < paginationInfo.totalPages - 1) {
      loadBanks(paginationInfo.pageNumber + 1, paginationInfo.pageSize);
    }
  };

  const handlePreviousPage = () => {
    if (paginationInfo.pageNumber > 0) {
      loadBanks(paginationInfo.pageNumber - 1, paginationInfo.pageSize);
    }
  };

  const startIndex = paginationInfo.pageNumber * paginationInfo.pageSize + 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Settings" breadcrumbItem="Banks" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <Button
                      color="primary"
                      className="add-btn float-end"
                      onClick={toggleModal}
                    >
                      <i className="ri-add-line align-bottom me-1" /> Add
                    </Button>
                  </h4>
                </CardHeader>

                <CardBody>
                  <Row className="g-4 mb-3">
                    <Col className="col-sm">
                      <div className="d-flex justify-content-sm-end">
                        <div className="search-box ms-2">
                          <input
                            type="text"
                            className="form-control search"
                            placeholder="Search by name/code..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive table-card mt-3 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBanks.length > 0 ? (
                          filteredBanks.map((bank, index) => (
                            <tr key={bank.id}>
                              <td>{startIndex + index}</td>
                              <td>{bank.name}</td>
                              <td>{bank.code}</td>
                              <td>
                                <div className="btn-group" role="group">
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => openModalForEdit(bank)}
                                  >
                                    <i className="ri-edit-box-line" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => confirmDeleteBank(bank.id, loadBanks)}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">No records found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    paginationInfo={paginationInfo}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    onPageChange={(pageNumber, pageSize) => loadBanks(pageNumber, pageSize)}
                    onPageSizeChange={(newPageSize) =>
                      setPaginationInfo((prev) => ({ ...prev, pageSize: newPageSize, pageNumber: 0 }))
                    }
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ConfirmDialog />

      <Modal isOpen={modalOpen} toggle={toggleModal} backdrop="static">
        <ModalHeader toggle={toggleModal}>
          {editingBankId ? "Edit Bank" : "Add Bank"}
        </ModalHeader>
        <ModalBody>
          <form
            onSubmit={(event) =>
              createOrUpdateBank(
                event,
                bankName,
                bankCode,
                editingBankId,
                loadBanks,
                toggleModal,
                setErrors
              )
            }
          >
            <FormGroup>
              <Label for="bankName">Bank Name</Label>
              <Input
                id="bankName"
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                invalid={!!errors.name}
              />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </FormGroup>

            <FormGroup>
              <Label for="bankCode">Bank Code</Label>
              <Input
                id="bankCode"
                type="text"
                value={bankCode}
                onChange={(e) => setBankCode(e.target.value)}
                invalid={!!errors.code}
              />
              {errors.code && <div className="invalid-feedback">{errors.code}</div>}
            </FormGroup>

            <Button type="submit" color="primary" className="float-end">
              {editingBankId ? "Update" : "Save"}
            </Button>
          </form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default Banks;
